import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import QrScanner from '../components/QrScanner';
import CoachShares from '../components/CoachShares';
import Sheet from '../components/Sheet';
import { PRIMARY } from '../components/CaptureSheets';
import { SectionTitle } from '../components/SettingsRows';
import { vnDate } from '../lib/coach';
import { useApp } from '../lib/AppContext';

const DEFAULT_SHARES = { meals: true, workouts: true, weight: false };
const toBody = (shares) => ({ share_meals: shares.meals, share_workouts: shares.workouts, share_weight: shares.weight });

function CoachCard({ coach, children }) {
  return (
    <section className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
      <div className="flex items-center gap-3">
        <Avatar name={coach.name} url={coach.avatar_url} size={52} />
        <div className="min-w-0">
          <p className="truncate text-lg font-bold">Coach {coach.name}</p>
          <p className="text-sm text-muted">{[coach.gym, coach.students_count != null && `${coach.students_count} học viên`].filter(Boolean).join(' · ') || coach.code}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

// ⑪ ⑫ Settings › Huấn luyện viên: connect with a coach's code (typed, scanned, or from /c/<code>), see what they
// will see, then "Chia sẻ với huấn luyện viên" once connected: what is shared, or stop sharing.
export default function MyCoachPage() {
  const { api, membership, setMembership, setProfile } = useApp();
  const { code: codeParam } = useParams();
  const [state, setState] = useState(null);
  const [code, setCode] = useState(codeParam || '');
  const [preview, setPreview] = useState(null);
  const [shares, setShares] = useState(DEFAULT_SHARES);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [switchTo, setSwitchTo] = useState(null);

  const load = useCallback(() => api.get('/coach_link').then(setState).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); }, [load]);

  const lookup = useCallback(async (value) => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await api.get(`/coaches/lookup?${new URLSearchParams({ code: value })}`));
      setShares(DEFAULT_SHARES);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [api]);
  useEffect(() => { if (codeParam) lookup(codeParam); }, [codeParam, lookup]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      setState(await fn());
      api.get('/membership').then(setMembership).catch(() => {});
      return true;
    } catch (e) {
      if (e.code === 'has_coach') setSwitchTo(e.data.current_coach_name);
      else setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const connect = (confirmSwitch = false) => act(() => api.post('/coach_link', { code: preview.code, ...toBody(shares), confirm_switch: confirmSwitch || undefined }))
    .then((ok) => {
      if (!ok) return;
      setPreview(null);
      setSwitchTo(null);
      // Connecting by code answers the role question (a student), so it isn't asked afterwards.
      setProfile((p) => (p ? { ...p, needsRole: false } : p));
    });

  const link = state?.link;
  const gift = state?.gift;
  const header = <PageHeader title="Huấn luyện viên" />;
  if (!state && !error) return <div className="flex flex-col gap-4">{header}<div className="skeleton h-40 rounded-[20px]" /></div>;

  // ⑫ the coach found by the code.
  if (preview) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {preview.own ? <p className="rounded-[20px] bg-base-100 p-4 text-sm">Đây là mã HLV của bạn. Hãy gửi cho học viên.</p> : (
          <>
            <CoachCard coach={preview}>
              <p className="flex items-start gap-2 rounded-[14px] bg-[#e3f1e9]/60 p-3 text-sm">
                <Icon name="info" className="mt-0.5 size-4 shrink-0" />
                Gói hiện tại của bạn giữ nguyên ({membership?.pro ? 'Pro cá nhân' : 'Free'}). Kết nối bằng mã không tốn phí.
              </p>
            </CoachCard>
            <SectionTitle>Coach sẽ xem được</SectionTitle>
            <CoachShares shares={shares} onChange={setShares} />
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <button type="button" className={PRIMARY} disabled={busy} onClick={() => connect()}>
              {busy && <span className="loading loading-spinner loading-sm" />} Gửi yêu cầu kết nối
            </button>
          </>
        )}
        <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={() => { setPreview(null); setError(null); }}>Nhập mã khác</button>
        <Sheet open={!!switchTo} onClose={() => setSwitchTo(null)} title={`Chuyển sang Coach ${preview.name}?`}>
          <div className="flex flex-col gap-4">
            <p className="text-sm">Bạn đang kết nối với Coach {switchTo}. Mỗi lúc chỉ có một huấn luyện viên: kết nối cũ sẽ kết thúc{gift ? ' và Pro Coach tặng cũng dừng' : ''}, Coach {switchTo} sẽ được báo.</p>
            <button type="button" className={PRIMARY} disabled={busy} onClick={() => connect(true)}>Chuyển sang Coach {preview.name}</button>
          </div>
        </Sheet>
      </div>
    );
  }

  if (link) {
    const pending = link.status === 'requested';
    return (
      <div className="flex flex-col gap-4">
        {header}
        <CoachCard coach={link.coach}>
          {pending ? (
            <>
              <p className="text-sm">Đang chờ Coach {link.coach.name} xác nhận</p>
              <button type="button" className="btn h-11 rounded-2xl" disabled={busy}
                onClick={() => act(() => api.del('/coach_link'))}>Huỷ yêu cầu</button>
            </>
          ) : (
            <p className="text-sm text-muted">
              {gift ? `Coach tặng Pro ${gift.until_cancelled ? 'đến khi Coach huỷ' : `đến hết ngày ${vnDate(gift.ends_at)}`}` : 'Đã kết nối · bạn dùng gói của riêng bạn'}
            </p>
          )}
        </CoachCard>
        {!pending && (
          <>
            {state.gift_ended_prompt?.can_ask && !link.renewal_requested && (
              <button type="button" className={PRIMARY} disabled={busy} onClick={() => act(() => api.post('/coach_link/request_renewal'))}>
                Nhờ Coach {link.coach.name} gia hạn
              </button>
            )}
            {link.renewal_requested && !gift && <p className="text-center text-sm text-muted">Đã nhờ Coach gia hạn</p>}
            <SectionTitle>Chia sẻ với huấn luyện viên</SectionTitle>
            {/* The toggle moves at once; the server's answer (or a reload on error) settles it. */}
            <CoachShares shares={link.shares} disabled={busy} onChange={(next) => {
              setState((s) => ({ ...s, link: { ...s.link, shares: next } }));
              act(() => api.patch('/coach_link', toBody(next))).then((ok) => { if (!ok) load(); });
            }} />
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <button type="button" className="mx-auto min-h-11 px-4 text-sm font-semibold text-error" disabled={busy}
              onClick={() => window.confirm(`Ngừng chia sẻ với Coach ${link.coach.name}? Kết nối sẽ kết thúc${gift ? ' và Pro Coach tặng cũng dừng' : ''}.`) && act(() => api.del('/coach_link'))}>
              Ngừng chia sẻ
            </button>
          </>
        )}
      </div>
    );
  }

  // ⑪ no coach yet.
  return (
    <div className="flex flex-col gap-4">
      {header}
      <section className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
        <p className="font-bold">Kết nối với huấn luyện viên</p>
        <p className="text-sm text-muted">Nhập mã HLV (ví dụ MINH-4821) hoặc quét mã QR của PT. Coach sẽ xem được nhật ký bạn chọn chia sẻ.</p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (code.trim()) lookup(code.trim()); }}>
          <label htmlFor="coach-code" className="sr-only">Mã HLV</label>
          <input id="coach-code" className="input h-12 flex-1 rounded-[14px] border-line text-base uppercase tracking-wider" placeholder="MINH-4821"
            autoCapitalize="characters" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} />
          <button type="submit" className="btn btn-primary h-12 rounded-[14px] border-0 px-5 font-bold" disabled={busy || !code.trim()}>Tiếp</button>
        </form>
        <button type="button" className="btn h-12 gap-2 rounded-[14px] border-line bg-base-100 font-bold" onClick={() => setScanning(true)}>
          <Icon name="scan" className="size-5" /> Quét QR của PT
        </button>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      </section>
      {scanning && <QrScanner onClose={() => setScanning(false)} onCode={(c) => { setScanning(false); setCode(c); lookup(c); }} />}
    </div>
  );
}
